import { fromLonLat, toLonLat } from "ol/proj";
import LineString from "ol/geom/LineString";
import { Fill, Stroke, Style } from "ol/style";
import Circle from "ol/geom/Circle";
import { CoordConverterGaussKruger } from "@orbis-lr-sdk/orbis-lr-sdk";
import { asArray } from "ol/color";
import { colorForSpeed } from "../util/colorScale";
import { maxSpeedFromCurvature } from "../curvature";
import { applyOverlapOffset } from "../util/arcOverlapOffset";

export function curvatureHoverStylesGenesis() {
  return curvatureHoverStyles();
}
export function curvatureHoverStylesOrbisSD() {
  return curvatureHoverStyles();
}
export function curvatureHoverStylesOrbisHD() {
  return curvatureHoverStyles();
}
export function curvatureAtJunctionHoverStylesOrbisSD() {
  return curvatureHoverStyles();
}

function curvatureCircleStyles(curvature, coordinates, colorLike) {
  const curvatureAbsolute = Math.abs(curvature);
  const radius = 10000 / curvatureAbsolute;

  const end = coordinates[coordinates.length - 1];
  const circleCoordinates = [end[0], end[1]];

  const lonLat = toLonLat(circleCoordinates);
  const lsLonLat = coordinates.map((coord) => toLonLat(coord));
  const midLonLat = movePerpendicular(lonLat, lsLonLat, radius, curvature < 0);

  const startMercator = circleCoordinates;
  const midMercator = fromLonLat(midLonLat);

  const distanceAB = new LineString([startMercator, midMercator]).getLength();
  const styles = [];
  const color = asArray(colorLike);
  const colorTransparent = color.slice();
  colorTransparent[3] = 0.3;

  styles.push(
    new Style({
      geometry: new Circle(midMercator, distanceAB),
      fill: new Fill({ color: colorTransparent }),
      stroke: new Stroke({ color: color, width: 2 }),
    }),
  );

  return styles;
}

export function curvatureHoverStyles() {
  return function (feature, resolution) {
    //3857
    const coordinates = applyOverlapOffset(
      feature.getGeometry().getCoordinates(),
      feature,
      resolution,
    );
    let curvature = feature.get("orbis-curvature:linear");
    if (curvature === undefined) {
      curvature = feature.get("curvature_at_junction");
    }

    // Guard: draw no circle unless curvature is a finite, non-zero number. A
    // non-numeric value (e.g. an LR-step-encoded "a#b" string) would otherwise reach
    // colorForSpeed as NaN and crash the spectral interpolation.
    const curvNum = Number(curvature);
    if (!Number.isFinite(curvNum) || curvNum === 0) {
      return [];
    }
    const [r, g, b] = colorForSpeed(maxSpeedFromCurvature(curvNum));
    return curvatureCircleStyles(curvNum, coordinates, `rgb(${r},${g},${b})`);
  };
}

function movePerpendicular(lonLat, coordinates, distance, isNegative) {
  const [longitude, latitude] = lonLat;
  const converter = new CoordConverterGaussKruger(longitude);
  const [lonGK, latGK] = converter.convertWgsToGaussKrueger(
    longitude,
    latitude,
  );
  // Find the segment containing the point
  let closestPrev = null;
  let closestNext = null;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const start = coordinates[i];
    const end = coordinates[i + 1];

    if (isPointOnSegment(start, end, lonLat)) {
      closestPrev = start;
      closestNext = end;
      break;
    }
  }

  if (!closestPrev || !closestNext) {
    throw new Error("Point is not on the linestring");
  }
  const closestPrevGK = converter.convertWgsToGaussKrueger(
    closestPrev[0],
    closestPrev[1],
  );
  const closestNextGK = converter.convertWgsToGaussKrueger(
    closestNext[0],
    closestNext[1],
  );

  // Calculate the angle of the line segment
  const dx = closestNextGK[0] - closestPrevGK[0];
  const dy = closestNextGK[1] - closestPrevGK[1];
  const angle = Math.atan2(dy, dx); // Angle in radians

  // Calculate the angle for perpendicular direction
  const perpendicularAngle = angle + Math.PI / 2; // 90 degrees in radians

  // Calculate new coordinates
  let newLongitudeGK = lonGK + distance * Math.cos(perpendicularAngle);
  let newLatitudeGK = latGK + distance * Math.sin(perpendicularAngle);

  if (isNegative) {
    newLongitudeGK = lonGK - distance * Math.cos(perpendicularAngle);
    newLatitudeGK = latGK - distance * Math.sin(perpendicularAngle);
  }

  // Return new lonLat
  return converter.convertGaussKruegerToWgs(newLongitudeGK, newLatitudeGK);
}
function isPointOnSegment(start, end, point) {
  const d1 = distance(start, point);
  const d2 = distance(end, point);
  const segmentLength = distance(start, end);

  // Check if point is within a small tolerance of the segment
  const tolerance = 0.0001; // Adjust as needed
  return Math.abs(d1 + d2 - segmentLength) < tolerance;
}

function distance(coord1, coord2) {
  return Math.sqrt(
    Math.pow(coord2[0] - coord1[0], 2) + Math.pow(coord2[1] - coord1[1], 2),
  );
}
